/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createHook,
	createActionsHook,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type Store,
	type StoreActionApi,
} from '@atlassian/react-sweet-state';
import type {
	BannerState,
	BottomRightPortalState,
	LayoutState,
	LeftSidebarState,
	PreviewPanelState,
	RightSidebarState,
} from '../../../common/types.tsx';
import { actions, type LayoutActions } from '../actions/index.tsx';

export const DEFAULT_LEFT_SIDEBAR_WIDTH = 240;
export const DEFAULT_RIGHT_SIDEBAR_WIDTH = 0;
export const DEFAULT_PREVIEW_PANEL_WIDTH = 400;

const initialState: LayoutState = {
	isChromeless: false,
	isFullscreen: false,
	banner: { height: 0, activeBanner: null },
	topNavigation: { isVisible: true },
	leftPanel: { isVisible: true, width: 0 },
	rightPanel: { isVisible: true, width: 0 },
	bottomRightCorner: { container: undefined, portals: [] },
	content: {
		leftSidebar: {
			width: DEFAULT_LEFT_SIDEBAR_WIDTH,
			isCollapsed: true,
			isVisible: true,
			isFlyoutOpen: false,
			hasUserPreference: false,
		},
		rightSidebar: {
			stackState: [],
		},
		previewPanel: {
			width: DEFAULT_PREVIEW_PANEL_WIDTH,
		},
	},
};

export const layoutStore: Store<
	LayoutState,
	{
		readonly setIsChromeless: (isChromeless: boolean) => Action<LayoutState>;
		readonly setIsFullscreen: (isFullscreen: boolean) => Action<LayoutState>;
		readonly showBanner: (newState: BannerState) => Action<LayoutState>;
		readonly dismissCurrentBanner: () => Action<LayoutState>;
		readonly setLeftSidebarState: (newState: Partial<LeftSidebarState>) => Action<LayoutState>;
		readonly setRightSidebarState: (newState: RightSidebarState) => Action<LayoutState>;
		readonly openRightSidebar: (
			panelId: string,
			width: number,
			isMinimised?: boolean,
			toTop?: boolean,
		) => Action<LayoutState>;
		readonly closeRightSidebar: (panelId: string) => Action<LayoutState>;
		readonly closeAllRightSidebar: () => Action<LayoutState>;
		readonly closeCurrentRightSidebar: () => Action<LayoutState>;
		readonly minimiseRightSidebar: (panelId: string) => Action<LayoutState>;
		readonly registerBottomRightCornerContainer: (
			ref: HTMLElement | null | undefined,
		) => Action<LayoutState>;
		readonly insertBottomRightCornerPortal: (
			item: BottomRightPortalState,
		) => Action<LayoutState, void, number>;
		readonly removeBottomRightCornerPortal: (id: string) => Action<LayoutState>;
		readonly setPreviewPanelState: (newState: Partial<PreviewPanelState>) => Action<LayoutState>;
	},
	unknown
> = createStore<LayoutState, LayoutActions>({
	initialState,
	actions,
	name: 'JiraLayoutStore',
});

export const useLayoutStoreActions: HookFunction<
	LayoutState,
	BoundActions<
		LayoutState,
		{
			readonly setIsChromeless: (isChromeless: boolean) => Action<LayoutState>;
			readonly setIsFullscreen: (isFullscreen: boolean) => Action<LayoutState>;
			readonly showBanner: (newState: BannerState) => Action<LayoutState>;
			readonly dismissCurrentBanner: () => Action<LayoutState>;
			readonly setLeftSidebarState: (newState: Partial<LeftSidebarState>) => Action<LayoutState>;
			readonly setRightSidebarState: (newState: RightSidebarState) => Action<LayoutState>;
			readonly openRightSidebar: (
				panelId: string,
				width: number,
				isMinimised?: boolean,
				toTop?: boolean,
			) => Action<LayoutState>;
			readonly closeRightSidebar: (panelId: string) => Action<LayoutState>;
			readonly closeAllRightSidebar: () => Action<LayoutState>;
			readonly closeCurrentRightSidebar: () => Action<LayoutState>;
			readonly minimiseRightSidebar: (panelId: string) => Action<LayoutState>;
			readonly registerBottomRightCornerContainer: (
				ref: HTMLElement | null | undefined,
			) => Action<LayoutState>;
			readonly insertBottomRightCornerPortal: (
				item: BottomRightPortalState,
			) => Action<LayoutState, void, number>;
			readonly removeBottomRightCornerPortal: (id: string) => Action<LayoutState>;
			readonly setPreviewPanelState: (newState: Partial<PreviewPanelState>) => Action<LayoutState>;
		}
	>,
	void
> = createHook(layoutStore, {
	selector: null,
});

const detailPanelWidthStore = createStore({
	name: 'detailPanelWidth',
	initialState: {
		width: 0,
	},
	actions: {
		setWidth:
			(
				value: number,
			): (({
				setState,
			}: StoreActionApi<{
				width: number;
			}>) => void) =>
			({ setState }) => {
				setState({ width: value });
			},
	},
});

export const useDetailPanelWidth: HookFunction<
	{
		width: number;
	},
	BoundActions<
		{
			width: number;
		},
		{
			setWidth: (value: number) => ({
				setState,
			}: StoreActionApi<{
				width: number;
			}>) => void;
		}
	>,
	void
> = createHook(detailPanelWidthStore);
export const useDetailPanelActions: HookActionsFunction<
	BoundActions<
		{
			width: number;
		},
		{
			setWidth: (value: number) => ({
				setState,
			}: StoreActionApi<{
				width: number;
			}>) => void;
		}
	>
> = createActionsHook(detailPanelWidthStore);
